import {
    computed,
    defineComponent,
    PropType,
    ref,
} from "vue";
import type { LogRole } from "../../core/types.js";
import {
    getRolePrimaryColorCss,
    roleColorGradientToCss,
} from "./roleColors.js";

export default defineComponent({
    name: "RolePickerDialog",

    props: {
        roles: { type: Array as PropType<LogRole[]>, required: true },
        selectedRoleIds: { type: Array as PropType<string[]>, required: true },
        disabledRoleIds: { type: Array as PropType<string[]>, default: () => [] },
        title: { type: String, required: true },
        subtitle: { type: String, default: "" },
        confirmLabel: { type: String, default: "Terminer" },
    },

    emits: {
        close: () => true,
        change: (_roleIds: string[]) => true,
    },

    setup(props, { emit }) {
        const query = ref("");
        const disabledRoles = computed(() => new Set(props.disabledRoleIds));
        const selectedRoles = computed(() => new Set(props.selectedRoleIds));
        const filteredRoles = computed(() => {
            const normalizedQuery = query.value.trim().toLowerCase();

            return props.roles.filter((role) => {
                if (disabledRoles.value.has(role.id))
                    return false;

                return !normalizedQuery
                    || role.id.includes(normalizedQuery)
                    || role.name.toLowerCase().includes(normalizedQuery);
            });
        });

        function toggleRole(roleId: string): void {
            const nextRoleIds = selectedRoles.value.has(roleId)
                ? props.selectedRoleIds.filter((selectedRoleId) => selectedRoleId !== roleId)
                : [ ...props.selectedRoleIds, roleId ];

            emit("change", nextRoleIds);
        }

        return () => (
            <div class="role-picker-backdrop" onClick={() => emit("close")}>
                <div class="role-picker-dialog" onClick={(event) => event.stopPropagation()}>
                    <header class="role-picker-header">
                        <div>
                            <strong>{props.title}</strong>
                            {props.subtitle && <span>{props.subtitle}</span>}
                        </div>
                        <button onClick={() => emit("close")} aria-label="Fermer">
                            <i class="material-symbols-outlined">close</i>
                        </button>
                    </header>

                    <label class="role-picker-search">
                        <i class="material-symbols-outlined">search</i>
                        <input
                            value={query.value}
                            autofocus
                            placeholder="Rechercher un rôle"
                            onInput={(event) => query.value = (event.target as HTMLInputElement).value}
                        />
                    </label>

                    <div class="role-picker-list">
                        {filteredRoles.value.map((role) => {
                            const selected = selectedRoles.value.has(role.id);

                            return (
                                <button key={role.id} class={{ selected }} onClick={() => toggleRole(role.id)}>
                                    <span
                                        class="role-picker-role-color"
                                        style={{
                                            "--role-color"          : getRolePrimaryColorCss(role),
                                            "--role-dot-background" : roleColorGradientToCss(role),
                                        } as Record<string, string>}
                                    />
                                    <span>
                                        <strong>{role.name}</strong>
                                        <small>{role.id}</small>
                                    </span>
                                    <i class="material-symbols-outlined">{selected ? "check_circle" : "radio_button_unchecked"}</i>
                                </button>
                            );
                        })}
                    </div>

                    <footer class="role-picker-actions">
                        <button onClick={() => emit("change", [])}>Vider</button>
                        <button class="primary" onClick={() => emit("close")}>{props.confirmLabel}</button>
                    </footer>
                </div>
            </div>
        );
    },
});
